/** @jsxImportSource @emotion/react */
import * as yup from "yup";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import * as authSelectors from "../../../auth/authSelectors";
import {
  bodyHeaderStyle,
  bodySectionStyle,
  bodyStyle,
  buttonWrapperStyle,
  formStyle,
} from "../../campaigns/components/MutateCampaignForm";
import {
  ButtonAction,
  ButtonInternalLink,
  ButtonPrimaryForwardedRef,
} from "../../../../components/NewButtons";
import { ActionSpinner } from "../../../../components/ActionSpinner";
import {
  ProductGroupListItem,
  ProductGroupFormType,
} from "../models/ProductGroup";
import { StyledText } from "../../../../assets/typography/typography";
import { FormRow } from "../../../common/hook-form/FormRow";
import { TextInput } from "../../../../components/forms/inputs/TextInput";
import { SelectCurrencyInput } from "../../../../components/forms/inputs/SelectCurrencyInput";
import { SelectCountryInput } from "../../../../components/forms/inputs/SelectCountryInput";
import { Disclosure } from "../../../../components/Disclosure";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { getCountrySpecificValues } from "../../../../util";
import { useGetCountries } from "../../../../service/api";
import { SpinnerBlock } from "../../../common/Spinner";
import { SelectProductsSelector } from "../../../../components/SelectProductsSelector";
import {
  useCreateProductGroup,
  useUpdateProductGroup,
} from "../service/product-groups-api";
import { Table } from "../../../../components/table-components/Table";
import { TableRowHeader } from "../../../../components/table-components/TableRowHeader";
import { colors } from "../../../../components/colors";
import { TableCellHeaderText } from "../../../../components/table-components/TableCellHeaderText";
import { TableBody } from "../../../../components/table-components/TableBody";
import { TableRow } from "../../../../components/table-components/TableRow";
import { TableCellText } from "../../../../components/table-components/TableCellText";
import { IconButton } from "../../../../components/IconButton";
import { css } from "@emotion/react";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { getErrorMessage } from "../../../../error-handler";
import { ErrorMessage } from "@hookform/error-message";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router";
import { routes } from "../../../../appRoutingHelpers";

type Props = {
  productGroup?: ProductGroupListItem;
};

const schema = yup.object({
  countryAlpha2Code: yup.string().required("Country is required"),
  currency: yup.string().required("Currency is required"),
  name: yup.string().required("Name is required"),
  products: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required("Product is required"),
        name: yup.string().required("Product name is required"),
      })
    )
    .min(1, "At least one product is required"),
});

const removeProductButtonStyle = css`
  display: flex;
  justify-content: end;
  align-items: center;
`;

const tableWrapperStyle = css`
  border: 1px solid ${colors.greyBorder};
  border-radius: 6px;
  overflow: hidden;
`;

export const MutateProductGroupForm = ({ productGroup }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const navigate = useNavigate();
  const { countryCode, currency } = getCountrySpecificValues();
  const { data: countries, isLoading: isLoadingCountries } = useGetCountries();
  const methods = useForm<ProductGroupFormType>({
    resolver: yupResolver(schema),
    defaultValues: {
      countryAlpha2Code: productGroup?.countryAlpha2Code || countryCode,
      currency: productGroup?.currency || currency,
      name: productGroup?.name || "",
      products: productGroup?.products || [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "products",
  });

  const { mutateAsync: updateProductGroup } = useUpdateProductGroup(
    productGroup?.id
  );

  const { mutateAsync: createProductGroup } = useCreateProductGroup();

  const onSubmit = async (data: ProductGroupFormType) => {
    const { products, ...rest } = data;
    try {
      if (productGroup) {
        await updateProductGroup({
          organizationKeyName: organizationKeyName,
          data: {
            ...rest,
            productIds: products.map((p) => p.id),
            id: productGroup.id,
          },
        });
      } else {
        await createProductGroup({
          organizationKeyName: organizationKeyName,
          data: {
            ...rest,
            productIds: products.map((p) => p.id),
          },
        });
      }
      navigate(routes.productGroups(organizationKeyName));
      toastr.success(
        "Success",
        `Product group ${productGroup ? "updated" : "created"} successfully`
      );
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  if (isLoadingCountries) {
    return <SpinnerBlock />;
  }

  return (
    <FormProvider {...methods}>
      <form css={formStyle} onSubmit={methods.handleSubmit(onSubmit)}>
        <div css={bodyStyle}>
          <div css={bodySectionStyle}>
            <div css={bodyHeaderStyle}>
              <StyledText className="bodyTextSemibold mb-0">
                Basic information
              </StyledText>
              <StyledText className="bodySubtextRegular mb-0">
                Name the product group and choose a country.
              </StyledText>
            </div>
            <FormRow>
              <TextInput name="name" label="Name" placeholder="Enter name" />
              <SelectCountryInput
                name="countryAlpha2Code"
                isDisabled={!!productGroup}
                onChange={(value) => {
                  const country = (countries || []).find(
                    (c) => c.countryCode === value
                  );
                  methods.setValue("currency", country?.currency || currency);
                }}
              />
            </FormRow>
            <Disclosure
              renderButton={(open) => (
                <ButtonPrimaryForwardedRef
                  icon={
                    open ? (
                      <FontAwesomeIcon icon={solid("chevron-up")} />
                    ) : (
                      <FontAwesomeIcon icon={solid("chevron-down")} />
                    )
                  }
                >
                  Advanced information
                </ButtonPrimaryForwardedRef>
              )}
            >
              <FormRow className="mt-2">
                <SelectCurrencyInput
                  name="currency"
                  isDisabled={!!productGroup}
                />
              </FormRow>
            </Disclosure>
          </div>
          <div css={bodySectionStyle}>
            <div css={bodyHeaderStyle}>
              <StyledText className="bodyTextSemibold mb-0">
                Products
              </StyledText>
              <StyledText className="bodySubtextRegular mb-0">
                Choose the products that will be included in the product group.
              </StyledText>
            </div>

            {fields.length > 0 && (
              <div css={tableWrapperStyle}>
                <Table>
                  <thead>
                    <TableRowHeader borderTop={false}>
                      <TableCellHeaderText className="pl-3">
                        Product name
                      </TableCellHeaderText>
                      <th />
                    </TableRowHeader>
                  </thead>
                  <TableBody borderBottom={false}>
                    {fields.map((field, i) => {
                      return (
                        <TableRow key={field.id}>
                          <TableCellText>
                            <StyledText
                              className="bodyTextRegular mb-0"
                              color={colors.textPrimary}
                            >
                              {field.name}
                            </StyledText>
                          </TableCellText>
                          <TableCellText className="pr-3">
                            <div css={removeProductButtonStyle}>
                              <IconButton
                                icon={<FontAwesomeIcon icon={solid("xmark")} />}
                                onClick={() => remove(i)}
                              />
                            </div>
                          </TableCellText>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
            <ErrorMessage
              name="products"
              render={({ message }) => (
                <StyledText
                  className="bodySubtextRegular mb-0"
                  color={colors.errorDark}
                >
                  {message}
                </StyledText>
              )}
            />
            <SelectProductsSelector
              filters={{
                classification: "generic",
              }}
              onSave={(products) => {
                append(products.map((p) => ({ id: p.id, name: p.title })));
              }}
              buttonText="Add products"
              modalHeader="Add products to product group"
            />
          </div>
        </div>
        <div css={buttonWrapperStyle}>
          <ButtonInternalLink to={".."}>Cancel</ButtonInternalLink>
          <ButtonAction type="submit" disabled={methods.formState.isSubmitting}>
            {methods.formState.isSubmitting ? (
              <ActionSpinner />
            ) : productGroup ? (
              "Update product group"
            ) : (
              "Create product group"
            )}
          </ButtonAction>
        </div>
      </form>
    </FormProvider>
  );
};
